import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Select, MenuItem, FormControl, InputLabel,
  Grid, Pagination, CircularProgress, Button, Switch, FormControlLabel, Alert,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { SmartToy, Refresh, CloudDownload } from '@mui/icons-material';
import toast from 'react-hot-toast';
import dayjs from '../utils/dayjs';
import { equityEngine } from '../services/api';

const PAGE_SIZE = 20;

const STAGE_COLORS = {
  SCANNING: '#9e9e9e', TREND_CONFIRMED: '#42a5f5', MOMENTUM_BUILDING: '#42a5f5',
  VOLUME_CONFIRMED: '#42a5f5', BREAKOUT_READY: '#ab47bc', WAITING_FOR_NEWS: '#ffa726',
  RISK_VALIDATION: '#ffa726', READY_TO_BUY: '#66bb6a', BUY_EXECUTED: '#2e7d32',
  POSITION_ACTIVE: '#2e7d32', TRAILING_STOP_ACTIVE: '#2e7d32', PROFIT_LOCKED: '#2e7d32',
  TARGET_HIT: '#2e7d32', EXITED: '#616161', REJECTED: '#c62828',
};

function StageChip({ stage }) {
  const color = STAGE_COLORS[stage] || '#9e9e9e';
  return <Chip size="small" label={(stage || 'SCANNING').replace(/_/g, ' ')} sx={{ bgcolor: `${color}22`, color, fontWeight: 700, fontSize: 11 }} />;
}

function DecisionChip({ decision }) {
  const map = { BUY: 'success', WATCH: 'warning', REJECT: 'error', NO_TRADE: 'default' };
  return <Chip size="small" label={decision} color={map[decision] || 'default'} />;
}

export default function EquityEnginePage() {
  const [status, setStatus] = useState(null);
  const [signals, setSignals] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(true);
  const [decisionFilter, setDecisionFilter] = useState('ALL');
  const [toggling, setToggling] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const [evaluations, setEvaluations] = useState([]);
  const [evalTotal, setEvalTotal] = useState(0);
  const [evalPage, setEvalPage] = useState(1);
  const [evalLoading, setEvalLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try { const r = await equityEngine.status(); setStatus(r.data.data); } catch { /* handled */ }
  }, []);

  const loadSignals = useCallback(async () => {
    setSignalsLoading(true);
    try {
      const params = {};
      if (decisionFilter !== 'ALL') params.decision = decisionFilter;
      const r = await equityEngine.signals(params);
      setSignals(r.data.data || []);
    } catch { /* handled */ } finally { setSignalsLoading(false); }
  }, [decisionFilter]);

  const loadEvaluations = useCallback(async () => {
    setEvalLoading(true);
    try {
      const r = await equityEngine.evaluations({ page: evalPage, limit: PAGE_SIZE });
      setEvaluations(r.data.data || []);
      setEvalTotal(r.data.total || 0);
    } catch { /* handled */ } finally { setEvalLoading(false); }
  }, [evalPage]);

  useEffect(() => { loadStatus(); const t = setInterval(loadStatus, 30000); return () => clearInterval(t); }, [loadStatus]);
  useEffect(() => { loadSignals(); const t = setInterval(loadSignals, 30000); return () => clearInterval(t); }, [loadSignals]);
  useEffect(() => { loadEvaluations(); }, [loadEvaluations]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const r = await equityEngine.setEnabled(!status?.enabled);
      toast.success(r.data.message);
      await loadStatus();
    } catch { /* toast shown by interceptor */ } finally { setToggling(false); }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const r = await equityEngine.runNow();
      toast.success(`Scan complete — BUY ${r.data.data.buyCount}, WATCH ${r.data.data.watchCount}, REJECT ${r.data.data.rejectCount}`);
      await Promise.all([loadSignals(), loadStatus(), loadEvaluations()]);
    } catch { /* toast shown by interceptor */ } finally { setRunningNow(false); }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await equityEngine.backfill();
      toast.success(r.data.message);
    } catch { /* toast shown by interceptor */ } finally { setBackfilling(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SmartToy color="primary" />
        <Typography variant="h5" fontWeight={700}>AI Equity Trading Engine</Typography>
        <Chip size="small" label="Phase 1a — Signal Only" color="info" />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        This engine only scans, scores, and recommends — it does <b>not</b> place any orders yet (Signal-Only mode).
        Auto-buy/auto-exit comes in a later phase once you've validated its decisions here.
      </Alert>

      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={<Switch checked={!!status?.enabled} onChange={handleToggle} disabled={toggling} />}
                label={status?.enabled ? 'Engine ON' : 'Engine OFF'}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary" display="block">Mode</Typography>
              <Chip size="small" label={status?.mode || 'SIGNAL_ONLY'} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary" display="block">Candles backfilled</Typography>
              <Typography fontWeight={700}>{status?.candleSymbolsBackfilled ?? 0} / 102</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary" display="block">Last scan</Typography>
              <Typography fontWeight={700} variant="body2">{status?.lastScanAt ? dayjs(status.lastScanAt).format('HH:mm:ss') : '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" variant="outlined" startIcon={<CloudDownload />} onClick={handleBackfill} disabled={backfilling}>
                Backfill
              </Button>
              <Button size="small" variant="contained" startIcon={runningNow ? <CircularProgress size={14} color="inherit" /> : <Refresh />} onClick={handleRunNow} disabled={runningNow}>
                Run Scan Now
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Recommendation Screen ─────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>Recommendation Screen</Typography>
            <Chip size="small" label={`${signals.length} stocks`} />
            <FormControl size="small" sx={{ ml: 'auto', minWidth: 140 }}>
              <InputLabel>Decision</InputLabel>
              <Select value={decisionFilter} label="Decision" onChange={e => setDecisionFilter(e.target.value)}>
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="BUY">BUY</MenuItem>
                <MenuItem value="WATCH">WATCH</MenuItem>
                <MenuItem value="REJECT">REJECT</MenuItem>
                <MenuItem value="NO_TRADE">NO TRADE</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            {signalsLoading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box> : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5', py: 0.8 } }}>
                    <TableCell>Stock</TableCell>
                    <TableCell align="right">CMP</TableCell>
                    <TableCell align="right">Buy Price</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">SL</TableCell>
                    <TableCell align="right">Target</TableCell>
                    <TableCell align="right">R:R</TableCell>
                    <TableCell align="right">Exp. Return</TableCell>
                    <TableCell align="right">Confidence</TableCell>
                    <TableCell align="right">Probability</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell>Sector</TableCell>
                    <TableCell>News</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell align="center">Decision</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signals.length === 0 ? (
                    <TableRow><TableCell colSpan={16} align="center" sx={{ py: 3 }}>
                      No signals yet — enable the engine and click "Run Scan Now", or wait for the next 1-minute cycle during market hours.
                    </TableCell></TableRow>
                  ) : signals.map(s => {
                    const reasons = (() => { try { return JSON.parse(s.reasons || '[]'); } catch { return []; } })();
                    return (
                      <TableRow key={s.symbol} hover sx={{ '& td': { py: 0.6 } }}>
                        <TableCell>
                          <Typography fontWeight={700} variant="body2">{s.symbol}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.name}</Typography>
                        </TableCell>
                        <TableCell align="right">₹{parseFloat(s.ltp || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">{s.entry_price ? `₹${parseFloat(s.entry_price).toLocaleString('en-IN')}` : '—'}</TableCell>
                        <TableCell align="right">{s.quantity || '—'}</TableCell>
                        <TableCell align="right">{s.stop_loss ? <Typography color="error.main" variant="body2">₹{parseFloat(s.stop_loss).toFixed(2)}</Typography> : '—'}</TableCell>
                        <TableCell align="right">{s.target ? <Typography color="success.main" variant="body2">₹{parseFloat(s.target).toFixed(2)}</Typography> : '—'}</TableCell>
                        <TableCell align="right">{s.rr_ratio ? `1:${parseFloat(s.rr_ratio).toFixed(1)}` : '—'}</TableCell>
                        <TableCell align="right">{s.expected_return_pct ? `${parseFloat(s.expected_return_pct).toFixed(1)}%` : '—'}</TableCell>
                        <TableCell align="right"><Typography fontWeight={700}>{s.confidence ?? '—'}%</Typography></TableCell>
                        <TableCell align="right">{s.probability_pct ?? '—'}%</TableCell>
                        <TableCell><Typography variant="caption">{s.trend_label || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{s.sector}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{s.news_sentiment || '—'}</Typography></TableCell>
                        <TableCell><StageChip stage={s.stage} /></TableCell>
                        <TableCell align="center"><DecisionChip decision={s.decision} /></TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <MuiTooltip title={reasons.join(' | ') || '—'}>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 260 }}>
                              {reasons[0] || '—'}
                            </Typography>
                          </MuiTooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Decision Log — every scan decision, including rejections ───────── */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Decision Log</Typography>
          <TableContainer>
            {evalLoading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box> : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5' } }}>
                    <TableCell>Time</TableCell><TableCell>Stock</TableCell><TableCell>Stage</TableCell>
                    <TableCell align="right">Confidence</TableCell><TableCell align="center">Decision</TableCell>
                    <TableCell>Reasoning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {evaluations.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>No evaluations logged yet</TableCell></TableRow>
                  ) : evaluations.map(e => (
                    <TableRow key={e.id} hover>
                      <TableCell><Typography variant="caption">{dayjs(e.cycle_time).format('DD/MM HH:mm:ss')}</Typography></TableCell>
                      <TableCell fontWeight={500}>{e.symbol}</TableCell>
                      <TableCell><StageChip stage={e.stage} /></TableCell>
                      <TableCell align="right">{e.confidence ?? '—'}</TableCell>
                      <TableCell align="center"><DecisionChip decision={e.decision} /></TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 400 }}>
                          {e.skip_reason || e.reasoning || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.max(1, Math.ceil(evalTotal / PAGE_SIZE))} page={evalPage} onChange={(_, v) => setEvalPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
